import { module as BaseModule } from "modujs";
import zipData from "../data/us-zips.json";

const BODY_SUCCESS_CLASS = "has-successfully-submitted";
const AJAX_SUCCESS_EVENT = "freeform-ajax-success";
const AJAX_BEFORE_SUCCESS_EVENT = "freeform-before-ajax-success";
const SUCCESS_SELECTOR = ".c-form__success";
const FREEFORM_RENDER_SUCCESS_EVENT = "freeform-render-success";
const FREEFORM_RENDER_ERROR_EVENT = "freeform-render-form-errors";
const ZIP_HANDLES = ["zip", "zipCode"];
const CITY_HANDLES = ["city"];
const STATE_HANDLES = ["state", "st"];
const PREFILL_STORAGE_KEY = "freeEstimate_prefillData";
const PREFILL_LOCKED_KEY = "freeEstimate_prefillLocked";
const PARTIAL_LEAD_RECAPTCHA_ACTION = "partial_lead";
const PARTIAL_LEAD_RECAPTCHA_MAX_AGE = 90 * 1000;
const STATE_NAMES = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
};

const ZIP_MAP = Array.isArray(zipData)
  ? zipData.reduce((acc, entry) => {
      const zip =
        entry?.zip ||
        entry?.zip_code ||
        entry?.postal_code ||
        entry?.postalCode ||
        null;

      if (zip === null || zip === undefined) {
        return acc;
      }

      const zipString = String(zip).padStart(5, "0");
      const city = entry?.city || entry?.city_name || "";
      const state = entry?.state || entry?.state_id || entry?.stateCode || "";

      if (zipString) {
        acc[zipString] = [city, state];
      }

      return acc;
    }, {})
  : {};
const ABANDON_REQUIRED_FIELDS = {
  firstName:
    'input[name="firstName"], input[name*="[firstName]"], input[name="form[firstName]"]',
  lastName:
    'input[name="lastName"], input[name*="[lastName]"], input[name="form[lastName]"]',
  zip:
    'input[name="zip"], input[name="zipCode"], input[name*="[zip]"], input[name*="[zipCode]"], input[name="form[zip]"], input[name="form[zipCode]"]',
};

const ABANDON_CONTACT_FIELDS = {
  email:
    'input[name="email"], input[name="emailAddress"], input[name*="[email]"], input[name*="[emailAddress]"], input[name="form[email]"], input[name="form[emailAddress]"]',
  phone:
    'input[name="phone"], input[name="phoneNumber"], input[name*="[phone]"], input[name*="[phoneNumber]"], input[name="form[phone]"], input[name="form[phoneNumber]"]',
};

function debounce(fn, delay = 150) {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), delay);
  };
}

export default class FreeEstimateForm extends BaseModule {
  init() {
    this.body = document.body;
    this.hasAppliedSuccess = false;
    this.successPanel = this.el.parentElement?.querySelector(
      ".l-free-estimate__success"
    );

    this.handleFormLoad = this.handleFormLoad.bind(this);
    this.handleAjaxSuccess = this.handleAjaxSuccess.bind(this);
    this.handleBeforeAjaxSuccess = this.handleBeforeAjaxSuccess.bind(this);
    this.handleRenderSuccess = this.handleRenderSuccess.bind(this);
    this.handleRenderErrors = this.handleRenderErrors.bind(this);
    this.applySuccessState = this.applySuccessState.bind(this);
    this.handleZipInput = this.handleZipInput.bind(this);
    this.handleCityOverride = this.handleCityOverride.bind(this);
    this.rebindZipAutofill = this.rebindZipAutofill.bind(this);
    this.rebindAbandonedLeadCapture = this.rebindAbandonedLeadCapture.bind(this);
    this.handlePrefillFieldInput = this.handlePrefillFieldInput.bind(this);
    this.handleDomMutations = debounce(() => this.rebindZipAutofill(), 50);
    this.teardownAbandonedLeadCapture = null;
    this.updateAbandonedLeadState = null;
    this.markAbandonedLeadSubmitted = null;
    this.abandonedLeadForm = null;
    this.prefillForm = null;

    this.handleFormLoad();
    this.setupMutationObserver();
    document.addEventListener(AJAX_SUCCESS_EVENT, this.handleAjaxSuccess);
    document.addEventListener(
      AJAX_BEFORE_SUCCESS_EVENT,
      this.handleBeforeAjaxSuccess
    );
    document.addEventListener(
      FREEFORM_RENDER_SUCCESS_EVENT,
      this.handleRenderSuccess
    );
    document.addEventListener(
      FREEFORM_RENDER_ERROR_EVENT,
      this.handleRenderErrors
    );

    this.evaluateInitialState();
    this.initAbandonedLeadCapture();
    this.initPrefill();
    this.startZipAutofillPolling();
  }

  destroy() {
    document.removeEventListener(AJAX_SUCCESS_EVENT, this.handleAjaxSuccess);
    document.removeEventListener(
      AJAX_BEFORE_SUCCESS_EVENT,
      this.handleBeforeAjaxSuccess
    );
    document.removeEventListener(
      FREEFORM_RENDER_SUCCESS_EVENT,
      this.handleRenderSuccess
    );
    document.removeEventListener(
      FREEFORM_RENDER_ERROR_EVENT,
      this.handleRenderErrors
    );

    this.teardownMutationObserver();
    this.teardownZipAutofill();
    this.stopZipAutofillPolling();

    if (typeof this.teardownAbandonedLeadCapture === "function") {
      this.teardownAbandonedLeadCapture();
    }

    if (this.prefillForm) {
      this.prefillForm.removeEventListener("input", this.handlePrefillFieldInput, true);
      this.prefillForm.removeEventListener("change", this.handlePrefillFieldInput, true);
      this.prefillForm = null;
    }

    this.markAbandonedLeadSubmitted = null;
  }

  handleFormLoad() {
    this.form = this.getForm();
    this.rebindZipAutofill();
    this.initPrefill();
  }
  evaluateInitialState() {
    if (this.el.dataset.freeEstimateHasSuccess === "true") {
      this.applySuccessState({ force: true });
      return;
    }

    const successBanner = this.el.querySelector(SUCCESS_SELECTOR);

    if (successBanner) {
      this.applySuccessState();
    }
  }

  handleRenderSuccess(event) {
    const form = this.getFormFromEvent(event);

    if (!form) {
      return;
    }

    this.form = form;
    this.applySuccessState();
    this.rebindZipAutofill();
    this.rebindAbandonedLeadCapture();
    if (typeof this.updateAbandonedLeadState === "function") {
      this.updateAbandonedLeadState();
    }
    this.initPrefill();
  }

  handleRenderErrors(event) {
    const form = this.getFormFromEvent(event);

    if (!form) {
      return;
    }

    this.form = form;
    this.el.dataset.freeEstimateHasSuccess = "false";
    this.el.removeAttribute("aria-hidden");
    this.el.style.removeProperty("display");

    if (this.successPanel) {
      this.successPanel.setAttribute("aria-hidden", "true");
      this.successPanel.style.setProperty("display", "none");
    }

    this.body.classList.remove(BODY_SUCCESS_CLASS);
    this.hasAppliedSuccess = false;
    this.rebindZipAutofill();
    this.rebindAbandonedLeadCapture();
    if (typeof this.updateAbandonedLeadState === "function") {
      this.updateAbandonedLeadState();
    }
    this.initPrefill();
  }

  handleBeforeAjaxSuccess(event) {
    const detail = event.detail || {};
    const form = this.getFormFromEvent(event);

    if (!form) {
      return;
    }

    this.form = form;
    if (typeof this.updateAbandonedLeadState === "function") {
      this.updateAbandonedLeadState();
    }
    this.rebindAbandonedLeadCapture();
  }

  initPrefill() {
    const form = this.getForm();
    if (!form) return;

    if (this.prefillForm && this.prefillForm === form) {
      return;
    }

    if (this.prefillForm) {
      this.prefillForm.removeEventListener("input", this.handlePrefillFieldInput, true);
      this.prefillForm.removeEventListener("change", this.handlePrefillFieldInput, true);
    }

    this.prefillForm = form;
    this.applyPrefillToForm(form);
    form.addEventListener("input", this.handlePrefillFieldInput, true);
    form.addEventListener("change", this.handlePrefillFieldInput, true);
  }

  handlePrefillFieldInput(event) {
    const target = event?.target;
    if (!target || !target.name) {
      return;
    }
    const value = (target.value || "").trim();
    this.updatePrefillStoredValue(target.name, value);
    this.lockPrefillField(target.name);
  }

  lockPrefillField(name) {
    if (!name) return;
    try {
      const locked = new Set(this.getLockedPrefillFields());
      if (!locked.has(name)) {
        locked.add(name);
        window.sessionStorage.setItem(
          PREFILL_LOCKED_KEY,
          JSON.stringify(Array.from(locked))
        );
      }
    } catch (e) {
      // ignore
    }
  }

  getLockedPrefillFields() {
    try {
      const raw = window.sessionStorage.getItem(PREFILL_LOCKED_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  updatePrefillStoredValue(name, value) {
    if (!name) return;
    try {
      const raw = window.sessionStorage.getItem(PREFILL_STORAGE_KEY);
      const data = raw ? JSON.parse(raw) : {};
      if (value) {
        data[name] = value;
      } else {
        delete data[name];
      }
      window.sessionStorage.setItem(PREFILL_STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      // ignore
    }
  }

  getPrefillData() {
    const data = {};

    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      params.forEach((value, key) => {
        if (value !== "") {
          data[key] = value;
        }
      });
    }

    try {
      const raw = window.sessionStorage.getItem(PREFILL_STORAGE_KEY);
      if (raw) {
        Object.assign(data, JSON.parse(raw) || {});
      }
    } catch (e) {
      // ignore
    }

    return data;
  }

  applyPrefillToForm(form) {
    const data = this.getPrefillData();
    const entries = Object.entries(data || {});
    if (!entries.length) return;

    const locked = new Set(this.getLockedPrefillFields());
    const escapeSelector = (value) => {
      if (window.CSS?.escape) {
        return window.CSS.escape(value);
      }
      return value.replace(/([\\\"\\#\\.\\[\\]\\=\\:\\,\\>\\+\\~\\*\\^\\$\\|\\(\\)\\s])/g, "\\$1");
    };

    const isTruthy = (value) => {
      if (value === true) return true;
      if (typeof value === "string") {
        return ["1", "true", "yes", "on"].includes(value.toLowerCase());
      }
      return Boolean(value);
    };

    entries.forEach(([name, value]) => {
      if (!name || value === null || value === undefined || value === "") {
        return;
      }
      if (locked.has(name)) {
        return;
      }

      const selector = `[name="${escapeSelector(name)}"]`;
      const fields = form.querySelectorAll(selector);
      fields.forEach((field) => {
        const type = (field.type || "").toLowerCase();
        if (type === "checkbox") {
          if (!field.checked && isTruthy(value)) {
            field.checked = true;
            this.triggerEvent(field, "change");
          }
          return;
        }
        if (type === "radio") {
          if (field.value === String(value)) {
            field.checked = true;
            this.triggerEvent(field, "change");
          }
          return;
        }

        const current = (field.value || "").trim();
        if (current !== "") {
          return;
        }
        field.value = value;
        this.triggerEvent(field, "input");
        this.triggerEvent(field, "change");
      });
    });
  }

  handleAjaxSuccess(event) {
    const detail = event.detail || {};
    const form = this.getFormFromEvent(event);
    const { response } = detail;

    if (!form) {
      return;
    }

    this.form = form;

    if (response?.onSuccess === "redirect-return-url" && event.cancelable) {
      event.preventDefault();
    }

    if (response?.success) {
      this.applySuccessState();
    }
  }

  getFormFromEvent(event) {
    const fromEvent = event?.detail?.form;
    if (fromEvent && this.el.contains(fromEvent)) {
      return fromEvent;
    }

    return this.getForm();
  }

  getForm() {
    const form = this.el.querySelector("form");
    return form || null;
  }

  applySuccessState({ force = false } = {}) {
    if (this.hasAppliedSuccess && !force) {
      return;
    }

    this.hasAppliedSuccess = true;
    this.el.dataset.freeEstimateHasSuccess = "true";

    if (!this.body.classList.contains(BODY_SUCCESS_CLASS)) {
      this.body.classList.add(BODY_SUCCESS_CLASS);
    }

    if (typeof this.markAbandonedLeadSubmitted === "function") {
      this.markAbandonedLeadSubmitted();
    }

    try {
      window.sessionStorage.removeItem(PREFILL_STORAGE_KEY);
      window.sessionStorage.removeItem(PREFILL_LOCKED_KEY);
    } catch (e) {
      // ignore
    }

    if (this.successPanel) {
      this.successPanel.removeAttribute("hidden");
      this.successPanel.setAttribute("aria-hidden", "false");
      this.successPanel.style.removeProperty("display");
      if (!force) {
        this.scrollToSuccessPanel();
      }
    }

    requestAnimationFrame(() => {
      this.el.querySelectorAll(SUCCESS_SELECTOR).forEach((banner) => {
        banner.setAttribute("hidden", "hidden");
      });
    });
  }

  scrollToSuccessPanel() {
    if (!this.successPanel) {
      return;
    }

    requestAnimationFrame(() => {
      const offset = 0;
      const top =
        this.successPanel.getBoundingClientRect().top +
        window.pageYOffset -
        offset;

      window.scrollTo({
        top,
        behavior: "smooth",
      });
    });
  }

  setupMutationObserver() {
    if (this.mutationObserver) return;
    this.mutationObserver = new MutationObserver(() => {
      this.handleDomMutations();
    });
    this.mutationObserver.observe(this.el, {
      childList: true,
      subtree: true,
    });
  }

  teardownMutationObserver() {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
  }

  startZipAutofillPolling() {
    this.stopZipAutofillPolling();
    this.zipAutofillInterval = setInterval(() => {
      this.rebindZipAutofill();
    }, 400);
  }

  stopZipAutofillPolling() {
    if (this.zipAutofillInterval) {
      clearInterval(this.zipAutofillInterval);
      this.zipAutofillInterval = null;
    }
  }

  rebindZipAutofill() {
    this.teardownZipAutofill();

    const form = this.getForm();
    if (!form) return;

    this.zipInput = this.findFieldInput(form, ZIP_HANDLES);
    this.cityInput = this.findFieldInput(form, CITY_HANDLES);
    this.stateInput = this.findFieldInput(form, STATE_HANDLES);
    this.autoCity = null;

    this.setStateDisabled(false);

    if (this.zipInput) {
      this.zipInput.addEventListener("input", this.handleZipInput);
      this.zipInput.addEventListener("blur", this.handleZipInput);
    }

    if (this.cityInput) {
      this.cityInput.addEventListener("input", this.handleCityOverride);
    }

    const hiddenZip =
      form.querySelector('input[type="hidden"][name*="zip"]')?.value || "";

    const initialZip =
      (this.zipInput && this.zipInput.value) || this.lastZip || hiddenZip || "";

    this.applyZipLookup(initialZip);
  }

  teardownZipAutofill() {
    if (this.zipInput) {
      this.zipInput.removeEventListener("input", this.handleZipInput);
      this.zipInput.removeEventListener("blur", this.handleZipInput);
    }
    if (this.cityInput) {
      this.cityInput.removeEventListener("input", this.handleCityOverride);
    }

    this.zipInput = null;
    this.cityInput = null;
    this.stateInput = null;
    if (this.stateMirror) {
      this.stateMirror.remove();
      this.stateMirror = null;
    }
    this.autoCity = null;
  }

  ensureHiddenMirror(input, key) {
    if (!input || !input.name) return null;

    const form = this.getForm();
    if (!form) return null;

    const attr = `data-zip-mirror-${key}`;
    let mirror = form.querySelector(`input[type="hidden"][${attr}]`);

    if (!mirror) {
      mirror = document.createElement("input");
      mirror.type = "hidden";
      mirror.setAttribute(attr, "true");
      mirror.name = input.name;
      input.insertAdjacentElement("afterend", mirror);
    }

    return mirror;
  }

  handleZipInput(event) {
    const value = event?.target?.value ?? "";
    this.applyZipLookup(value);
  }

  handleCityOverride() {
    if (!this.cityInput) return;
    if (
      this.autoCity &&
      this.cityInput.value &&
      this.cityInput.value !== this.autoCity
    ) {
      this.autoCity = null;
    }
  }

  applyZipLookup(rawZip = "") {
    const zip = (rawZip || "").replace(/\D+/g, "").slice(0, 5);

    if (!zip || zip.length < 5) {
      this.setStateDisabled(false);
      this.setStateValue("");
      this.setCityValue("");
      return;
    }

    this.lastZip = zip;

    const match = ZIP_MAP[zip];

    if (!match) {
      this.setStateDisabled(false);
      // Do not clear manual inputs; preserve what the user types.
      return;
    }

    const [city, state] = match;
    this.setStateDisabled(true);
    this.setStateValue(state || "");
    this.setCityValue(city || "");
  }

  findFieldInput(form, handles = []) {
    const selectors = handles.flatMap((handle) => [
      `input[name="${handle}"]`,
      `input[name$="[${handle}]"]`,
      `input[name*="[${handle}]"]`,
      `select[name="${handle}"]`,
      `select[name$="[${handle}]"]`,
      `select[name*="[${handle}]"]`,
      `[data-field="${handle}"] input`,
      `[data-field="${handle}"] select`,
    ]);

    const field =
      form.querySelector(selectors.join(", ")) ||
      form.querySelector(`.c-form__column.is-${handles[0]} input`) ||
      form.querySelector(`.c-form__column.is-${handles[0]} select`);

    return field || null;
  }

  setStateValue(state) {
    if (!this.stateInput) return;
    const code = (state || "").toUpperCase();
    const name = STATE_NAMES[code] || state || "";

    if (this.stateInput.tagName === "SELECT") {
      const options = Array.from(this.stateInput.options || []);
      const match =
        options.find((opt) => opt.value.toUpperCase() === code) ||
        options.find((opt) => opt.value.toLowerCase() === name.toLowerCase()) ||
        options.find(
          (opt) => opt.textContent.trim().toLowerCase() === name.toLowerCase()
        );

      if (match) {
        this.stateInput.value = match.value;
      } else {
        this.stateInput.value = code || name;
      }
    } else {
      this.stateInput.value = code || name;
    }

    if (this.stateMirror) {
      this.stateMirror.value = code || name;
    }

    this.triggerEvent(this.stateInput, "input");
    this.triggerEvent(this.stateInput, "change");
  }

  setStateDisabled(disabled) {
    if (!this.stateInput) return;

    this.stateInput.disabled = Boolean(disabled);
    if ("readOnly" in this.stateInput) {
      this.stateInput.readOnly = Boolean(disabled);
    }

    if (disabled) {
      this.stateMirror = this.ensureHiddenMirror(this.stateInput, "state");
    } else if (this.stateMirror) {
      this.stateMirror.remove();
      this.stateMirror = null;
    }
  }

  setCityValue(city) {
    if (!this.cityInput) return;

    if (this.stateInput && this.stateInput.disabled) {
      this.cityInput.value = city;
      this.autoCity = city;
      this.triggerEvent(this.cityInput, "input");
      this.triggerEvent(this.cityInput, "change");
      return;
    }
  }

  triggerEvent(el, type) {
    if (!el) return;
    const evt = new Event(type, { bubbles: true });
    el.dispatchEvent(evt);
  }

  initAbandonedLeadCapture() {
    const enabled = this.el.dataset.abandonedLeadsEnabled === "1";
    const endpoint = this.el.dataset.abandonedLeadsEndpoint;
    const form = this.getForm();

    if (!enabled || !endpoint || !form) {
      this.abandonedLeadForm = null;
      return;
    }

    if (this.abandonedLeadForm && this.abandonedLeadForm === form) {
      return;
    }

    this.abandonedLeadForm = form;

    const state = {
      readyForPartial: false,
      hasSubmitted: false,
      partialSent: false,
      cachedPayload: null,
      sending: false,
      recaptchaToken: null,
      recaptchaGeneratedAt: 0,
      recaptchaInflight: null,
      recaptchaLoader: null,
    };

    const submittedFlag = "freeEstimate_submitted";
    const partialFlag = "freeEstimate_partialSent";
    const cachedFieldsKey = "freeEstimate_cachedFields";

    try {
      state.hasSubmitted = window.sessionStorage.getItem(submittedFlag) === "1";
      state.partialSent = window.sessionStorage.getItem(partialFlag) === "1";
      const savedFields = window.sessionStorage.getItem(cachedFieldsKey);
      if (savedFields) {
        state.cachedPayload = JSON.parse(savedFields);
      }
    } catch (e) {
      // sessionStorage may be blocked; fail silently
    }

    const setSubmitted = () => {
      state.hasSubmitted = true;
      try {
        window.sessionStorage.setItem(submittedFlag, "1");
      } catch (e) {
        // ignore
      }
    };
    this.markAbandonedLeadSubmitted = setSubmitted;

    const setPartialSent = () => {
      state.partialSent = true;
      try {
        window.sessionStorage.setItem(partialFlag, "1");
      } catch (e) {
        // ignore
      }
    };

    const getFieldValue = (selector) => {
      const candidates = form.querySelectorAll(selector);
      for (const input of candidates) {
        if (!input) {
          continue;
        }
        if (input.type === "checkbox" || input.type === "radio") {
          if (!input.checked) {
            continue;
          }
        }
        const value = (input.value || "").trim();
        if (value) {
          return value;
        }
      }
      return "";
    };

    const collectAllFilledFields = () => {
      const fields = {};
      const elements = form.querySelectorAll("input, select, textarea");
      const handles = [
        "firstName",
        "lastName",
        "zip",
        "zipCode",
        "email",
        "emailAddress",
        "phone",
        "phoneNumber",
      ];

      elements.forEach((el) => {
        if (!el.name || el.disabled) {
          return;
        }
        const type = (el.type || "").toLowerCase();
        if (["button", "submit", "reset", "file", "image"].includes(type)) {
          return;
        }
        if ((type === "checkbox" || type === "radio") && !el.checked) {
          return;
        }
        const value = (el.value || "").trim();
        if (!value) {
          return;
        }
        fields[el.name] = value;

        handles.forEach((handle) => {
          if (
            el.name === handle ||
            el.name === `form[${handle}]` ||
            el.name.includes(`[${handle}]`)
          ) {
            fields[handle] = value;
          }
        });
      });
      return fields;
    };

    const buildPayload = (fields) => {
      const payload = { ...fields };

      const hasFirstName =
        Boolean(payload.firstName) ||
        Boolean(getFieldValue(ABANDON_REQUIRED_FIELDS.firstName));
      const hasLastName =
        Boolean(payload.lastName) ||
        Boolean(getFieldValue(ABANDON_REQUIRED_FIELDS.lastName));
      const zipValue =
        getFieldValue(ABANDON_REQUIRED_FIELDS.zip) ||
        payload.zipCode ||
        payload.zip;
      const emailValue =
        getFieldValue(ABANDON_CONTACT_FIELDS.email) ||
        payload.emailAddress ||
        payload.email;
      const phoneValue =
        getFieldValue(ABANDON_CONTACT_FIELDS.phone) ||
        payload.phoneNumber ||
        payload.phone;

      if (!hasFirstName || !hasLastName || !zipValue || (!emailValue && !phoneValue)) {
        return null;
      }

      payload.firstName =
        payload.firstName || getFieldValue(ABANDON_REQUIRED_FIELDS.firstName);
      payload.lastName =
        payload.lastName || getFieldValue(ABANDON_REQUIRED_FIELDS.lastName);
      payload.zip = zipValue;
      if (zipValue) {
        payload.zipCode = zipValue;
      }
      if (emailValue) {
        payload.email = emailValue;
        payload.emailAddress = emailValue;
      }
      if (phoneValue) {
        payload.phone = phoneValue;
        payload.phoneNumber = phoneValue;
      }

      payload.sourceUrl = window.location.href;
      payload.timestamp = new Date().toISOString();

      return payload;
    };

    const getCsrfToken = () => {
      const metaParam = document
        .querySelector('meta[name="csrf-param"]')
        ?.getAttribute("content");
      const metaToken = document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content");

      if (metaParam && metaToken) {
        return { param: metaParam, token: metaToken };
      }

      if (window.Craft?.csrfTokenName && window.Craft?.csrfTokenValue) {
        return {
          param: window.Craft.csrfTokenName,
          token: window.Craft.csrfTokenValue,
        };
      }

      const hiddenCsrf = form.querySelector(
        'input[type="hidden"][name*="CSRF"], input[type="hidden"][name*="csrf"]'
      );

      if (hiddenCsrf && hiddenCsrf.name && hiddenCsrf.value) {
        return { param: hiddenCsrf.name, token: hiddenCsrf.value };
      }

      return null;
    };

    const csrfState = {
      param: null,
      token: null,
      fetchedAt: 0,
      inflight: null,
    };
    const csrfTtlMs = 10 * 60 * 1000;
    const csrfEndpoint = "/actions/users/session-info";

    const applyCsrfState = (param, token) => {
      if (!param || !token) return;
      csrfState.param = param;
      csrfState.token = token;
      csrfState.fetchedAt = Date.now();
    };

    const extractCsrfFromSessionInfo = (json) => {
      if (!json || typeof json !== "object") return null;
      const param =
        json.csrfTokenName ||
        json.csrfParam ||
        json.csrfParamName ||
        json.csrfTokenParam ||
        null;
      const token =
        json.csrfTokenValue ||
        json.csrfToken ||
        json.csrfTokenNameValue ||
        null;
      if (param && token) {
        return { param, token };
      }
      return null;
    };

    const refreshCsrfToken = () => {
      const now = Date.now();
      if (csrfState.inflight) {
        return csrfState.inflight;
      }
      if (csrfState.token && now - csrfState.fetchedAt < csrfTtlMs) {
        return Promise.resolve({
          param: csrfState.param,
          token: csrfState.token,
        });
      }

      csrfState.inflight = fetch(csrfEndpoint, {
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
        },
      })
        .then((res) => res.json())
        .then((json) => {
          const extracted = extractCsrfFromSessionInfo(json);
          if (extracted) {
            applyCsrfState(extracted.param, extracted.token);
            return extracted;
          }
          const fallback = getCsrfToken();
          if (fallback) {
            applyCsrfState(fallback.param, fallback.token);
            return fallback;
          }
          return null;
        })
        .catch(() => {
          const fallback = getCsrfToken();
          if (fallback) {
            applyCsrfState(fallback.param, fallback.token);
            return fallback;
          }
          return null;
        })
        .finally(() => {
          csrfState.inflight = null;
        });

      return csrfState.inflight;
    };

    const persistCachedFields = (fields) => {
      try {
        window.sessionStorage.setItem(cachedFieldsKey, JSON.stringify(fields || {}));
      } catch (e) {
        // ignore persistence failures
      }
    };

    const getRecaptchaConfig = () => {
      const container = form.querySelector(
        '[data-captcha="recaptcha"][data-sitekey]'
      );
      const siteKey =
        container?.dataset.sitekey || this.el.dataset.recaptchaSiteKey;

      if (!siteKey) {
        return null;
      }

      return {
        siteKey,
        version: container?.dataset.version || "v3",
      };
    };

    const loadRecaptcha = (siteKey) => {
      if (window.grecaptcha?.ready && window.grecaptcha?.execute) {
        return Promise.resolve(window.grecaptcha);
      }

      if (state.recaptchaLoader) {
        return state.recaptchaLoader;
      }

      state.recaptchaLoader = new Promise((resolve) => {
        const existingScript = document.querySelector(
          'script[src^="https://www.google.com/recaptcha/api.js"]'
        );
        const script = existingScript || document.createElement("script");

        const handleLoad = () => {
          if (window.grecaptcha?.ready && window.grecaptcha?.execute) {
            resolve(window.grecaptcha);
          } else {
            resolve(null);
          }
        };

        script.addEventListener("load", handleLoad, { once: true });
        script.addEventListener("error", () => resolve(null), { once: true });

        if (!existingScript) {
          const url = new URL("https://www.google.com/recaptcha/api.js");
          url.searchParams.set("render", siteKey);
          script.src = url.toString();
          script.async = true;
          script.defer = true;
          script.dataset.partialLeadRecaptcha = "true";
          document.head.appendChild(script);
        }
      });

      return state.recaptchaLoader;
    };

    const refreshRecaptchaToken = () => {
      const config = getRecaptchaConfig();
      if (!config || config.version !== "v3") {
        return Promise.resolve(null);
      }

      const tokenIsFresh =
        state.recaptchaToken &&
        Date.now() - state.recaptchaGeneratedAt <
          PARTIAL_LEAD_RECAPTCHA_MAX_AGE;

      if (tokenIsFresh) {
        return Promise.resolve(state.recaptchaToken);
      }

      if (state.recaptchaInflight) {
        return state.recaptchaInflight;
      }

      state.recaptchaInflight = loadRecaptcha(config.siteKey)
        .then((grecaptcha) => {
          if (!grecaptcha) {
            return null;
          }

          return new Promise((resolve) => {
            grecaptcha.ready(() => {
              grecaptcha
                .execute(config.siteKey, {
                  action: PARTIAL_LEAD_RECAPTCHA_ACTION,
                })
                .then((token) => {
                  state.recaptchaToken = token || null;
                  state.recaptchaGeneratedAt = token ? Date.now() : 0;
                  resolve(state.recaptchaToken);
                })
                .catch(() => resolve(null));
            });
          });
        })
        .finally(() => {
          state.recaptchaInflight = null;
        });

      return state.recaptchaInflight;
    };

    const updateReadyState = () => {
      if (state.hasSubmitted || state.partialSent) {
        return;
      }

      const latest = collectAllFilledFields();
      const merged = { ...(state.cachedPayload || {}), ...latest };
      const payload = buildPayload(merged);

      state.cachedPayload = payload || merged;
      state.readyForPartial = Boolean(payload);

      persistCachedFields(state.cachedPayload);

      if (state.readyForPartial) {
        refreshRecaptchaToken();
      }
    };

    this.updateAbandonedLeadState = updateReadyState;

    const debouncedUpdate = debounce(updateReadyState, 150);

    form.addEventListener("input", debouncedUpdate, true);
    form.addEventListener("blur", debouncedUpdate, true);
    form.addEventListener("change", debouncedUpdate, true);
    refreshCsrfToken();
    refreshRecaptchaToken();

    const sendPartialLead = () => {
      if (state.hasSubmitted || state.partialSent || state.sending) {
        return;
      }

      updateReadyState();

      if (!state.readyForPartial || !state.cachedPayload) {
        return;
      }

      state.sending = true;

      refreshRecaptchaToken()
        .then((recaptchaToken) => {
          if (!recaptchaToken) {
            return;
          }

          const payload = {
            ...state.cachedPayload,
            "g-recaptcha-response": recaptchaToken,
          };
          const csrf =
            (csrfState.param && csrfState.token
              ? { param: csrfState.param, token: csrfState.token }
              : null) || getCsrfToken();
          if (csrf) {
            payload[csrf.param] = csrf.token;
          }

          const params = new URLSearchParams(payload);
          const beaconPayload = new Blob([params.toString()], {
            type: "application/x-www-form-urlencoded",
          });
          const postOptions = {
            method: "POST",
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
            body: params,
            keepalive: true,
            credentials: "same-origin",
          };

          return fetch(endpoint, postOptions)
            .then((res) => {
              return res
                .json()
                .catch(() => ({}))
                .then((json) => ({ status: res.status, ok: json?.ok, json }));
            })
            .then((result) => {
              if (result.ok) {
                setPartialSent();
                return;
              }
              if (navigator.sendBeacon) {
                const sent = navigator.sendBeacon(endpoint, beaconPayload);
                if (sent) {
                  setPartialSent();
                }
              }
            })
            .catch(() => {
              if (navigator.sendBeacon) {
                const sent = navigator.sendBeacon(endpoint, beaconPayload);
                if (sent) {
                  setPartialSent();
                }
              }
            });
        })
        .finally(() => {
          state.sending = false;
        });
    };

    // End helper setup

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        sendPartialLead();
      } else {
        refreshCsrfToken();
      }
    };

    const handlePageHide = () => {
      sendPartialLead();
    };

    const handleBeforeUnload = () => {
      sendPartialLead();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener("beforeunload", handleBeforeUnload);

    this.teardownAbandonedLeadCapture = ({ send = true } = {}) => {
      if (send) {
        sendPartialLead();
      }

      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("beforeunload", handleBeforeUnload);

      form.removeEventListener("input", debouncedUpdate, true);
      form.removeEventListener("blur", debouncedUpdate, true);
      form.removeEventListener("change", debouncedUpdate, true);
    };

    updateReadyState();
  }

  rebindAbandonedLeadCapture() {
    const form = this.getForm();
    if (!form || form === this.abandonedLeadForm) {
      return;
    }

    if (typeof this.teardownAbandonedLeadCapture === "function") {
      this.teardownAbandonedLeadCapture({ send: false });
      this.teardownAbandonedLeadCapture = null;
    }

    this.initAbandonedLeadCapture();
  }
}
